<script setup lang="ts">
import type { AuctionItemEntity, AuctionListingItemEntity } from '~/apiClient';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntity | AuctionListingItemEntity>, required: true },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const { isLoggedIn } = useAuthUtils();

// DATA
const hasBids = computed(() => {
    if (!isLoggedIn.value) return false;

    const isDetailItem = (a: AuctionItemEntity | AuctionListingItemEntity): a is AuctionItemEntity => !!a.userMeta;

    if (isDetailItem(auction)) return !!(auction.userMeta?.maxBidder || auction.userMeta?.outBid);

    return !!auction.bidInfo;
});
const hasReserveReachedNotice = computed(() => !!auction.reservePriceReached);
const hasReserveNotReachedNotice = computed(() => hasBids.value && auction.reservePriceReached === false);
const hasReserveNotice = computed(() => hasReserveReachedNotice.value || hasReserveNotReachedNotice.value);

const iconColor = computed(() => (hasReserveReachedNotice.value ? 'text-green-600' : 'text-yellow-600'));
</script>

<template>
    <BaseTooltip
        v-if="hasReserveNotice"
        :text="tt(hasReserveReachedNotice ? 'auction.reservePrice.tooltip.reached' : 'auction.reservePrice.tooltip.notReached')"
    >
        <span class="flex items-center gap-0.5 rounded-full border border-solid border-neutral-400 bg-white px-2 py-1 text-sm">
            <UIcon class="text-base" name="lucide:shield-alert" :class="iconColor" />

            <i18n-t
                scope="global"
                tag="span"
                class="text-highlighted"
                :keypath="hasReserveReachedNotice ? 'auction.reservePrice.reached.base' : 'auction.reservePrice.notReached.base'"
            >
                <span class="font-semibold">
                    {{ tt(hasReserveReachedNotice ? 'auction.reservePrice.reached.highlight' : 'auction.reservePrice.notReached.highlight') }}
                </span>
            </i18n-t>

            <UIcon class="text-muted" name="lucide:info" />
        </span>
    </BaseTooltip>
</template>
